import {
    Autocomplete,
    Box,
    Button,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
} from "@mui/material";
import { IService, ServiceStatus } from "../../../../models/service";
import { useIntl } from "react-intl";
import Messages from "../../../../localization/Messages";
import { useCallback, useMemo, useRef, useState } from "react";
import { ArrowDropDown } from "@mui/icons-material";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { IInvoice } from "../../../../models/invoice";
import { IBooking } from "../../../../../src/models/booking";
import { DateFormat, getDateMoment } from "../../../../../src/helpers/dateHelper";
import { useDestinationTimeZone } from "../../../../hooks/useDestinationTimeZone";
import "./ServiceButtonMenuGroup.css";

export type ServiceMenuButtonGroupProps = {
    row: IService;
    onClick: () => void;
    menuActions: {
        deleteService: (serviceid: string) => void;
        viewInvoice: (invoiceId: number) => void;
        attachInvoice: (serviceId: string, invoiceId: number) => void;
        detachInvoice: (serviceId: string) => void;
        showServices: (service: IService) => void;
        swapBooking: (serviceId: string, bookingId: number) => void;
    };
    invoices: IInvoice[];
    bookings: IBooking[];
};

export const ServiceButtonMenuGroup = ({
    row,
    onClick,
    menuActions,
    invoices,
    bookings,
}: ServiceMenuButtonGroupProps) => {
    const timeZone = useDestinationTimeZone();
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const withConfirm = useConfirmDialog();
    const [invoice, setInvoice] = useState<IInvoice>();
    const [booking, setBooking] = useState<IBooking>();
    const [attachServiceDialog, setAttachServiceDialog] = useState(false);
    const [detachServiceDialog, setDetachServiceDialog] = useState(false);
    const [swapBookingDialog, setSwapBookingDialog] = useState(false);

    const serviceNotOpen = row.status !== ServiceStatus.OPEN;
    const serviceAttached = !!row.invoiceId;

    const handleCloseDialog = () => {
        setAttachServiceDialog(false);
        setDetachServiceDialog(false);
        setSwapBookingDialog(false);
    };

    const dialogTitle = useMemo(() => {
        if (attachServiceDialog) {
            return intl.formatMessage(Messages.attachToInvoice);
        } else if (detachServiceDialog) {
            return intl.formatMessage(Messages.detachFromInvoice);
        } else if (swapBookingDialog) {
            return intl.formatMessage(Messages.swapBooking);
        }
    }, [attachServiceDialog, detachServiceDialog, swapBookingDialog, intl]);

    const dialogContent = useMemo(() => {
        if (attachServiceDialog) {
            return (
                <Box display={"flex"} flexDirection="column" gap={2} justifyContent={"center"}>
                    <span>{intl.formatMessage(Messages.attachToInvoiceMessage)}</span>
                    <Autocomplete
                        id="attach-invoice-select"
                        options={invoices}
                        value={invoices.find(inv => inv.id === invoice?.id) ?? null}
                        onChange={(_, value) => setInvoice(value ?? undefined)}
                        getOptionLabel={o => `${o.internalInvoiceNumber} - ${o.invoiceCompanyName}`}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="filled"
                                label={intl.formatMessage(Messages.invoice)}
                            />
                        )}
                        renderOption={(props, o) => (
                            <li {...props} key={o.id}>
                                {o.internalInvoiceNumber} {o.invoiceCompanyName}
                            </li>
                        )}
                    />
                </Box>
            );
        } else if (detachServiceDialog) {
            return <span>{intl.formatMessage(Messages.detachFromInvoiceMessage)}</span>;
        } else if (swapBookingDialog) {
            return (
                <Box display={"flex"} flexDirection="column" gap={2} justifyContent={"center"}>
                    <span>{intl.formatMessage(Messages.swapBookingMessage)}</span>
                    <Autocomplete
                        id="booking-swap-select"
                        options={bookings}
                        value={bookings.find(b => b.id === booking?.id) ?? null}
                        onChange={(_, value) => setBooking(value ?? undefined)}
                        getOptionLabel={o =>
                            `#${o.id} - ${getDateMoment(
                                o.actualArrivalTime ?? o.arrivalTime,
                                { timeZone },
                            ).format(DateFormat.CLIENT_DATE_TIME)} - ${o.shipName}`
                        }
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={params => (
                            <TextField
                                {...params}
                                variant="filled"
                                label={intl.formatMessage(Messages.booking)}
                            />
                        )}
                        renderOption={(props, o) => (
                            <li {...props} key={o.id}>
                                #{o.id} -{" "}
                                {getDateMoment(o.actualArrivalTime ?? o.arrivalTime, { timeZone }).format(
                                    DateFormat.CLIENT_DATE_TIME,
                                )}{" "}
                                - {o.shipName}
                            </li>
                        )}
                    />
                </Box>
            );
        }
    }, [
        attachServiceDialog,
        detachServiceDialog,
        swapBookingDialog,
        intl,
        invoices,
        invoice,
        bookings,
        booking,
        timeZone,
    ]);

    const dialogConfirmAction = useMemo(() => {
        if (attachServiceDialog) {
            return () => {
                invoice?.id && menuActions.attachInvoice(row.id ?? "", invoice?.id);
                handleCloseDialog();
            };
        } else if (detachServiceDialog) {
            return () => {
                menuActions.detachInvoice(row.id ?? "");
                handleCloseDialog();
            };
        } else if (swapBookingDialog) {
            return () => {
                booking?.id && menuActions.swapBooking(row.id ?? "", booking?.id ?? 0);
                handleCloseDialog();
            };
        }
    }, [
        attachServiceDialog,
        booking,
        detachServiceDialog,
        invoice,
        menuActions,
        row.id,
        swapBookingDialog,
    ]);

    const handleToggle = useCallback(() => {
        setOpen(prev => !prev);
    }, []);

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (menuButtonRef.current && menuButtonRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    return (
        <>
            <ButtonGroup variant="contained">
                <Tooltip
                    sx={{ pointer: "auto" }}
                    title={
                        row.finalized
                            ? intl.formatMessage(Messages.serviceEditUnavailableMessage)
                            : serviceNotOpen
                            ? intl.formatMessage(Messages.serviceEditUnavailableMessage2)
                            : ""
                    }
                >
                    <span>
                        <Button
                            variant="contained"
                            onClick={onClick}
                            disabled={Boolean(row.finalized) || serviceNotOpen}
                        >
                            {intl.formatMessage(Messages.edit)}
                        </Button>
                    </span>
                </Tooltip>
                <Button size="small" ref={menuButtonRef} onClick={handleToggle}>
                    <ArrowDropDown />
                </Button>
            </ButtonGroup>
            <Menu anchorEl={menuButtonRef?.current} open={open} onClose={handleToggle}>
                <MenuItem
                    onClick={() => row.invoiceId && menuActions.viewInvoice(row.invoiceId)}
                    disabled={!row.invoiceId}
                >
                    {intl.formatMessage(Messages.viewInvoice)}
                </MenuItem>

                <Tooltip
                    title={
                        row.finalized
                            ? intl.formatMessage(Messages.serviceEditUnavailableMessage)
                            : serviceNotOpen
                            ? intl.formatMessage(Messages.serviceEditUnavailableMessage2)
                            : ""
                    }
                >
                    <span>
                        <MenuItem
                            disabled={!!row.finalized || serviceNotOpen}
                            onClick={() =>
                                row.id &&
                                !row.finalized &&
                                withConfirm({
                                    message: intl.formatMessage(Messages.confirmServiceDeleteText),
                                    onConfirm: () => menuActions.deleteService(row.id ?? ""),
                                })
                            }
                        >
                            {intl.formatMessage(Messages.delete)}
                        </MenuItem>
                    </span>
                </Tooltip>

                <Tooltip
                    title={
                        serviceAttached
                            ? intl.formatMessage(Messages.attachServiceDisabledMessage)
                            : ""
                    }
                >
                    <span>
                        <MenuItem
                            onClick={() => !serviceAttached && setAttachServiceDialog(true)}
                            disabled={serviceAttached}
                        >
                            {intl.formatMessage(Messages.attachToInvoice)}
                        </MenuItem>
                    </span>
                </Tooltip>

                <Tooltip
                    title={
                        !serviceAttached
                            ? intl.formatMessage(Messages.serviceNotAttachedMessage)
                            : serviceNotOpen
                            ? intl.formatMessage(Messages.detachServiceDisabledMessage)
                            : ""
                    }
                >
                    <span>
                        <MenuItem
                            onClick={() =>
                                !serviceNotOpen && serviceAttached && setDetachServiceDialog(true)
                            }
                            disabled={serviceNotOpen || !serviceAttached}
                        >
                            {intl.formatMessage(Messages.detachFromInvoice)}
                        </MenuItem>
                    </span>
                </Tooltip>
                <MenuItem
                    onClick={event => {
                        handleClose(event);
                        menuActions.showServices(row);
                    }}
                >
                    {intl.formatMessage(Messages.showServices)}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setSwapBookingDialog(true);
                    }}
                >
                    {intl.formatMessage(Messages.swapBooking)}
                </MenuItem>
            </Menu>
            <Dialog
                open={dialogContent !== undefined}
                id="service-actions-dialog"
                onClose={() => {
                    handleCloseDialog();
                    setInvoice(undefined);
                    setBooking(undefined);
                }}
            >
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>{dialogContent}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleCloseDialog();
                        }}
                    >
                        {intl.formatMessage(Messages.cancel)}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            dialogConfirmAction && dialogConfirmAction();
                        }}
                    >
                        {intl.formatMessage(Messages.confirm)}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
